/**
 * Markdown Editor Interface
 * Simple markdown editor with preview support
 */

import { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
    Bold,
    Italic,
    Strikethrough,
    List,
    ListOrdered,
    Quote,
    Code,
    Link,
    Heading1,
    Heading2,
    Heading3,
    Eye,
    Edit,
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface MarkdownEditorProps {
    value: string;
    onChange: (value: string) => void;
    disabled?: boolean;
    placeholder?: string;
    className?: string;
    defaultView?: 'edit' | 'preview';
}

export function MarkdownEditor({
    value,
    onChange,
    disabled = false,
    placeholder = 'Write your markdown here...',
    className,
    defaultView = 'edit',
}: MarkdownEditorProps) {
    const [activeTab, setActiveTab] = useState<'edit' | 'preview'>(defaultView);
    const [textareaRef, setTextareaRef] = useState<HTMLTextAreaElement | null>(null);

    // Insert markdown syntax at cursor position
    const insertMarkdown = useCallback((prefix: string, suffix: string = '') => {
        if (!textareaRef || disabled) return;

        const start = textareaRef.selectionStart;
        const end = textareaRef.selectionEnd;
        const selectedText = value.substring(start, end);
        const newText = value.substring(0, start) + prefix + selectedText + suffix + value.substring(end);

        onChange(newText);

        // Set cursor position after insertion
        setTimeout(() => {
            textareaRef.focus();
            textareaRef.setSelectionRange(
                start + prefix.length,
                start + prefix.length + selectedText.length
            );
        }, 0);
    }, [value, onChange, textareaRef, disabled]);

    // Toolbar actions
    const handleBold = () => insertMarkdown('**', '**');
    const handleItalic = () => insertMarkdown('*', '*');
    const handleStrikethrough = () => insertMarkdown('~~', '~~');
    const handleBulletList = () => insertMarkdown('- ');
    const handleNumberedList = () => insertMarkdown('1. ');
    const handleQuote = () => insertMarkdown('> ');
    const handleCode = () => insertMarkdown('`', '`');
    const handleLink = () => insertMarkdown('[', '](url)');
    const handleH1 = () => insertMarkdown('# ');
    const handleH2 = () => insertMarkdown('## ');
    const handleH3 = () => insertMarkdown('### ');

    // Simple markdown to HTML renderer
    const renderMarkdown = (text: string): string => {
        if (!text) return '<p class="text-muted-foreground">Nothing to preview</p>';

        let html = text
            // Escape HTML
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            // Headers
            .replace(/^### (.+)$/gm, '<h3 class="text-lg font-semibold mt-4 mb-2">$1</h3>')
            .replace(/^## (.+)$/gm, '<h2 class="text-xl font-semibold mt-4 mb-2">$1</h2>')
            .replace(/^# (.+)$/gm, '<h1 class="text-2xl font-bold mt-4 mb-2">$1</h1>')
            // Bold and Italic
            .replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
            .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
            .replace(/\*(.+?)\*/g, '<em>$1</em>')
            // Strikethrough
            .replace(/~~(.+?)~~/g, '<del>$1</del>')
            // Inline code
            .replace(/`(.+?)`/g, '<code class="px-1 py-0.5 rounded bg-muted font-mono text-sm">$1</code>')
            // Links
            .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" class="text-primary underline" target="_blank">$1</a>')
            // Blockquote
            .replace(/^> (.+)$/gm, '<blockquote class="border-l-4 border-muted-foreground/30 pl-4 italic text-muted-foreground">$1</blockquote>')
            // Lists
            .replace(/^- (.+)$/gm, '<li class="ml-4">$1</li>')
            .replace(/^\d+\. (.+)$/gm, '<li class="ml-4 list-decimal">$1</li>')
            // Line breaks
            .replace(/\n\n/g, '</p><p class="mt-2">')
            .replace(/\n/g, '<br/>');

        return `<div class="prose prose-sm dark:prose-invert max-w-none"><p>${html}</p></div>`;
    };

    return (
        <div className={cn('rounded-md border', className)}>
            <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'edit' | 'preview')}>
                {/* Toolbar */}
                <div className="flex items-center justify-between border-b px-2 py-1">
                    <div className="flex items-center gap-0.5">
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={handleH1}
                            disabled={disabled || activeTab === 'preview'}
                            title="Heading 1"
                        >
                            <Heading1 className="h-4 w-4" />
                        </Button>
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={handleH2}
                            disabled={disabled || activeTab === 'preview'}
                            title="Heading 2"
                        >
                            <Heading2 className="h-4 w-4" />
                        </Button>
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={handleH3}
                            disabled={disabled || activeTab === 'preview'}
                            title="Heading 3"
                        >
                            <Heading3 className="h-4 w-4" />
                        </Button>
                        <div className="mx-1 h-4 w-px bg-border" />
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={handleBold}
                            disabled={disabled || activeTab === 'preview'}
                            title="Bold"
                        >
                            <Bold className="h-4 w-4" />
                        </Button>
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={handleItalic}
                            disabled={disabled || activeTab === 'preview'}
                            title="Italic"
                        >
                            <Italic className="h-4 w-4" />
                        </Button>
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={handleStrikethrough}
                            disabled={disabled || activeTab === 'preview'}
                            title="Strikethrough"
                        >
                            <Strikethrough className="h-4 w-4" />
                        </Button>
                        <div className="mx-1 h-4 w-px bg-border" />
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={handleBulletList}
                            disabled={disabled || activeTab === 'preview'}
                            title="Bullet List"
                        >
                            <List className="h-4 w-4" />
                        </Button>
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={handleNumberedList}
                            disabled={disabled || activeTab === 'preview'}
                            title="Numbered List"
                        >
                            <ListOrdered className="h-4 w-4" />
                        </Button>
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={handleQuote}
                            disabled={disabled || activeTab === 'preview'}
                            title="Quote"
                        >
                            <Quote className="h-4 w-4" />
                        </Button>
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={handleCode}
                            disabled={disabled || activeTab === 'preview'}
                            title="Code"
                        >
                            <Code className="h-4 w-4" />
                        </Button>
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={handleLink}
                            disabled={disabled || activeTab === 'preview'}
                            title="Link"
                        >
                            <Link className="h-4 w-4" />
                        </Button>
                    </div>
                    <TabsList className="h-8">
                        <TabsTrigger value="edit" className="h-7 px-2 text-xs">
                            <Edit className="mr-1 h-3 w-3" />
                            Edit
                        </TabsTrigger>
                        <TabsTrigger value="preview" className="h-7 px-2 text-xs">
                            <Eye className="mr-1 h-3 w-3" />
                            Preview
                        </TabsTrigger>
                    </TabsList>
                </div>

                {/* Content */}
                <TabsContent value="edit" className="m-0">
                    <Textarea
                        ref={setTextareaRef}
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        disabled={disabled}
                        placeholder={placeholder}
                        className="min-h-[200px] rounded-none border-0 font-mono text-sm focus-visible:ring-0"
                    />
                </TabsContent>
                <TabsContent value="preview" className="m-0">
                    <div
                        className="min-h-[200px] p-3 text-sm"
                        dangerouslySetInnerHTML={{ __html: renderMarkdown(value) }}
                    />
                </TabsContent>
            </Tabs>
        </div>
    );
}
